
import { useEffect, useState } from 'react';
import { Plus, Loader2, Pencil, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetCloseButton,
} from '@/components/ui/sheet';
import { authFetch } from '@/lib/auth-client';
import {
  buildManualPropDescription,
  expandManualPropAcrossGames,
  supportsFanOut,
  type MatchScope,
} from '@/services/events/main-event/manual-prop.template';
import { MANUAL_PROP_PRESETS } from '@/services/events/main-event/manual-prop.presets';
import { NFL_PROP_PRESETS } from '@/services/events/main-event/nfl-prop-presets';
import { NflPlayerPicker } from '@/components/admin/NflPlayerPicker';
import {
  manualStatOptions,
  reconcileStatCategory,
  type ManualStatOption,
} from '@/services/events/main-event/manual-stat-options';

export type ManualPropType =
  | 'MANUAL_TEAM_THRESHOLD'
  | 'MANUAL_MATCH_THRESHOLD'
  | 'MANUAL_PLAYER_BOOLEAN'
  | 'MANUAL_GENERIC';

export type ManualPickMode = 'BUY_ONLY' | 'YES_NO';

export interface ManualModalGame {
  id: string;
  homeTeam: string;
  awayTeam: string;
}

export interface ManualPropForEdit {
  id: string;
  description: string;
  costCoins: number;
  bonusMultiplier: number;
  propType: ManualPropType;
  pickMode: ManualPickMode;
  subjectTeam: string | null;
  subjectPlayer: string | null;
  playerId: string | null;
  playerPosition?: string | null;
  threshold: number | null;
  statCategory: string | null;
  gameId: string | null;
}

export interface ManualPredictionModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  mainEventId: string;
  undercardId: string;
  games: ManualModalGame[];
  pointsPerCorrectPick: number;
  /** Drives which preset set is offered. NFL gets auto-settleable presets. */
  sport?: string;
  existingProp?: ManualPropForEdit | null;
  onClose: () => void;
  onSuccess: (msg: string) => void;
}

const MATCH_TEMPLATES: { value: ManualPropType; label: string; hint: string }[] = [
  { value: 'MANUAL_TEAM_THRESHOLD', label: 'Team Threshold', hint: 'Single team in a match' },
  { value: 'MANUAL_MATCH_THRESHOLD', label: 'Match Threshold', hint: 'Either team in a match' },
  { value: 'MANUAL_PLAYER_BOOLEAN', label: 'Player Boolean', hint: 'A specific player' },
  { value: 'MANUAL_GENERIC', label: 'Custom', hint: 'Free-form (round or match)' },
];

/**
 * A prop already in the DB may name a stat outside the current catalog — an
 * older category, or one typed in when this field was free text. Editing it
 * must not silently re-point it at something else, so its value is carried
 * into the list as an extra entry rather than reconciled away.
 */
const withExistingValue = (options: ManualStatOption[], current: string): ManualStatOption[] => {
  if (!current || options.some((o) => o.id === current)) return options;
  const legacy: ManualStatOption = {
    id: current,
    label: `${current} (existing)`,
    phrase: current.toLowerCase().replace(/_/g, ' '),
    defaultThreshold: 1,
    step: 0.5,
    settlement: 'MANUAL',
    note: 'Not in the catalog for this template — settles by hand.',
  };
  return [legacy, ...options];
};

/**
 * Compute the form's customDescription field for edit mode.
 * If the prop's stored description matches what `buildManualPropDescription` would produce
 * from the structured fields, the prop is templated — leave override blank so structural
 * edits regenerate. Otherwise it was a custom override, pre-fill it for editing.
 */
const detectCustomOverride = (
  prop: ManualPropForEdit,
  games: ManualModalGame[]
): string => {
  const game = prop.gameId ? games.find((g) => g.id === prop.gameId) : null;
  const templated = buildManualPropDescription({
    propType: prop.propType,
    subjectTeam: prop.subjectTeam,
    subjectPlayer: prop.subjectPlayer,
    threshold: prop.threshold,
    statCategory: prop.statCategory,
    homeTeam: game?.homeTeam ?? null,
    awayTeam: game?.awayTeam ?? null,
  });
  if (templated && templated === prop.description) return '';
  return prop.description ?? '';
};

const initialForm = (
  existing?: ManualPropForEdit | null,
  games: ManualModalGame[] = [],
  sport?: string
) => {
  const propType = (existing?.propType ?? 'MANUAL_MATCH_THRESHOLD') as ManualPropType;
  return {
    propType,
    // Manual predictions force YES_NO. BUY_ONLY is preserved on the schema for legacy
    // odds-based props, but is intentionally not surfaced in the manual modal.
    pickMode: 'YES_NO' as ManualPickMode,
    costCoins: existing?.costCoins ?? 100,
    bonusMultiplier: existing?.bonusMultiplier ?? 1.5,
    subjectTeam: existing?.subjectTeam ?? '',
    subjectPlayer: existing?.subjectPlayer ?? '',
    playerId: existing?.playerId ?? '',
    // Kept so a stat change can tell whether the bound player still qualifies
    // without re-querying — the search list is empty once a pick is made.
    playerPosition: existing?.playerPosition ?? '',
    threshold: existing?.threshold == null ? '' : String(existing.threshold),
    // An existing prop keeps its own stat. A new one opens on the first stat
    // the sport offers for this template, so the field is never pre-filled
    // with a category the sport can't settle (NFL opening on GOALS).
    statCategory: existing?.statCategory ?? manualStatOptions(sport, propType)[0]?.id ?? '',
    customDescription: existing ? detectCustomOverride(existing, games) : '',
    gameId: existing?.gameId ?? '',
    applyToAllGames: false,
    matchScope: 'EITHER_TEAM' as MatchScope,
  };
};

export function ManualPredictionModal({
  open,
  mode,
  mainEventId,
  undercardId,
  games,
  pointsPerCorrectPick,
  sport,
  existingProp,
  onClose,
  onSuccess,
}: ManualPredictionModalProps) {
  const [form, setForm] = useState(() => initialForm(existingProp, games, sport));
  const [submitting, setSubmitting] = useState(false);
  /**
   * Validation and submit failures render inside the sheet.
   *
   * They used to go to an onError() callback that set a banner on the page
   * UNDERNEATH this modal — so a rejected submit looked like the form had
   * simply reset itself and nothing had happened.
   */
  const [submitError, setSubmitError] = useState('');

  // Reset form when opening with new context
  useEffect(() => {
    if (open) {
      setForm(initialForm(existingProp, games, sport));
      // Stays mounted while `open` toggles, so a rejected submit would otherwise
      // still be showing its alert the next time the sheet opens.
      setSubmitError('');
    }
  }, [open, existingProp, games, sport]);

  // When admin changes the template, clear orphaned fields that don't apply.
  const setPropType = (next: ManualPropType) => {
    setForm((f) => {
      const wantsTeam = next === 'MANUAL_TEAM_THRESHOLD';
      const wantsPlayer = next === 'MANUAL_PLAYER_BOOLEAN';
      const wantsThreshold = next !== 'MANUAL_GENERIC';
      const wantsStat = next !== 'MANUAL_GENERIC';
      // Custom + applyToAllGames doesn't need a gameId; MATCH_THRESHOLD without fan-out requires one.
      // Don't clear gameId on type change — admin may have selected a game intentionally.
      return {
        ...f,
        propType: next,
        subjectTeam: wantsTeam ? f.subjectTeam : '',
        subjectPlayer: wantsPlayer ? f.subjectPlayer : '',
        playerId: wantsPlayer ? f.playerId : '',
        playerPosition: wantsPlayer ? f.playerPosition : '',
        threshold: wantsThreshold ? f.threshold : '',
        // Each template settles from a different source, so the stat list
        // changes with it. Carry the selection over where the new list has an
        // equivalent rather than dropping the admin back to the top.
        statCategory: wantsStat
          ? reconcileStatCategory(f.statCategory, manualStatOptions(sport, next, f.matchScope))
          : '',
        // Fan-out only applies to MATCH_THRESHOLD and Custom; clear when switching away.
        applyToAllGames: supportsFanOut(next) ? f.applyToAllGames : false,
      };
    });
  };

  /**
   * EACH_TEAM rewrites a match prop into one prop per side, which settles
   * against a single team's box score. The stat has to follow, or both props
   * resolve to the same game-wide total while reading as per-team questions.
   */
  const setMatchScope = (next: MatchScope) => {
    setForm((f) => ({
      ...f,
      matchScope: next,
      statCategory: reconcileStatCategory(
        f.statCategory,
        manualStatOptions(sport, f.propType, next)
      ),
    }));
  };

  const fanOutEligible = supportsFanOut(form.propType);
  const showGameSelector = !form.applyToAllGames;
  const showThresholdField = form.propType === 'MANUAL_TEAM_THRESHOLD' || form.propType === 'MANUAL_MATCH_THRESHOLD' || form.propType === 'MANUAL_PLAYER_BOOLEAN';
  const showStatField = showThresholdField;
  // A fanned-out team threshold takes its team from each game, so asking for
  // one here would be a field the admin must leave blank — which is exactly
  // the dead end this flow used to be.
  const teamIsPerGame = form.propType === 'MANUAL_TEAM_THRESHOLD' && form.applyToAllGames;
  const showSubjectTeam = form.propType === 'MANUAL_TEAM_THRESHOLD' && !teamIsPerGame;
  const showSubjectPlayer = form.propType === 'MANUAL_PLAYER_BOOLEAN';
  const customIsRequired = form.propType === 'MANUAL_GENERIC';

  // Stats this template can actually be settled from, plus whatever an
  // existing prop already names. The chosen one drives the threshold's step
  // and the note under the field.
  const statOptions = withExistingValue(
    manualStatOptions(sport, form.propType, form.matchScope),
    form.statCategory
  );
  const selectedStat = statOptions.find((o) => o.id === form.statCategory);

  /**
   * Move the line to the new stat's default, but only when the admin hasn't
   * set one of their own — 4.5 touchdowns must not silently become the line
   * for passing yards, and a hand-typed 275 must survive.
   */
  const setStatCategory = (next: string) => {
    setForm((f) => {
      const from = statOptions.find((o) => o.id === f.statCategory);
      const to = statOptions.find((o) => o.id === next);
      const untouched = f.threshold === '' || Number(f.threshold) === from?.defaultThreshold;
      return {
        ...f,
        statCategory: next,
        threshold: untouched && to ? String(to.defaultThreshold) : f.threshold,
      };
    });
  };

  // Live preview: for MATCH_THRESHOLD, use the fan-out helper to render scope-aware
  // preview lines (1 line for EITHER_TEAM/COMBINED_TOTAL, 2 for EACH_TEAM).
  // For other propTypes, fall back to single-line buildManualPropDescription.
  const previewGame = form.gameId ? games.find((g) => g.id === form.gameId) : null;
  const fanOutPreviewGame = form.applyToAllGames && games.length > 0 ? games[0] : null;
  const previewLines: string[] = (() => {
    // A fanned-out team threshold has no single team to name, so the
    // single-line builder returns '' and the admin sees no preview at all.
    // Expand it against the first game to show the real pair of questions.
    if (teamIsPerGame) {
      const refGame = fanOutPreviewGame ?? games[0];
      if (!refGame) return [];
      return expandManualPropAcrossGames(
        {
          propType: form.propType,
          threshold: form.threshold === '' ? null : Number(form.threshold),
          statCategory: form.statCategory,
        },
        [{ id: refGame.id, homeTeam: refGame.homeTeam, awayTeam: refGame.awayTeam }]
      )
        .map((d) => d.description)
        .filter(Boolean);
    }
    if (form.propType !== 'MANUAL_MATCH_THRESHOLD') {
      const desc = buildManualPropDescription({
        propType: form.propType,
        subjectTeam: form.subjectTeam,
        subjectPlayer: form.subjectPlayer,
        threshold: form.threshold === '' ? null : Number(form.threshold),
        statCategory: form.statCategory,
        customDescription: form.customDescription,
        homeTeam: previewGame?.homeTeam ?? null,
        awayTeam: previewGame?.awayTeam ?? null,
      });
      return desc ? [desc] : [];
    }
    // MATCH_THRESHOLD: use scope-aware expansion against either the chosen single
    // game or the first fan-out game (for an example).
    const refGame = previewGame ?? fanOutPreviewGame;
    if (!refGame) {
      // No game context yet — show the placeholder template
      const desc = buildManualPropDescription({
        propType: form.propType,
        threshold: form.threshold === '' ? null : Number(form.threshold),
        statCategory: form.statCategory,
        customDescription: form.customDescription,
      });
      return desc ? [desc] : [];
    }
    const drafts = expandManualPropAcrossGames(
      {
        propType: form.propType,
        threshold: form.threshold === '' ? null : Number(form.threshold),
        statCategory: form.statCategory,
        customDescription: form.customDescription,
      },
      [{ id: refGame.id, homeTeam: refGame.homeTeam, awayTeam: refGame.awayTeam }],
      form.matchScope
    );
    return drafts.map((d) => d.description).filter(Boolean);
  })();

  // A prop BOOSTS the pick it sits on — it is not a second scoring track. The
  // reward is the INCREMENT on top of the pick's own points, and it pays only
  // when that pick was correct. Showing multiplier x base here overstated it:
  // a 1.5x prop reads as 15 points when it actually adds 5, turning a correct
  // 10-point pick into 15.
  const validMultiplier = Number.isFinite(form.bonusMultiplier) && form.bonusMultiplier > 0;
  const boostedTotal = validMultiplier ? form.bonusMultiplier * pointsPerCorrectPick : 0;
  const estimatedPoints = validMultiplier ? boostedTotal - pointsPerCorrectPick : 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitError('');
    setSubmitting(true);

    const thresholdNum = form.threshold === '' ? null : Number(form.threshold);
    if (form.threshold !== '' && Number.isNaN(thresholdNum)) {
      setSubmitError('Threshold must be a number');
      setSubmitting(false);
      return;
    }

    // Only send fields relevant to the chosen propType so we don't trip the
    // server's "orphan field" cross-validation.
    const wantsTeam = form.propType === 'MANUAL_TEAM_THRESHOLD';
    const wantsPlayer = form.propType === 'MANUAL_PLAYER_BOOLEAN';
    const wantsThreshold =
      form.propType === 'MANUAL_TEAM_THRESHOLD' ||
      form.propType === 'MANUAL_MATCH_THRESHOLD' ||
      form.propType === 'MANUAL_PLAYER_BOOLEAN';
    const wantsStat = wantsThreshold;

    const payload: Record<string, unknown> = {
      propType: form.propType,
      pickMode: form.pickMode,
      costCoins: form.costCoins,
      bonusMultiplier: form.bonusMultiplier,
      subjectTeam: wantsTeam ? form.subjectTeam.trim() || null : null,
      subjectPlayer: wantsPlayer ? form.subjectPlayer.trim() || null : null,
      // Settlement resolves a player prop through this id alone. Sent as null
      // for non-NFL sports, which have no roster to bind against.
      playerId: wantsPlayer ? form.playerId || null : null,
      threshold: wantsThreshold ? thresholdNum : null,
      statCategory: wantsStat ? form.statCategory.trim() || null : null,
      customDescription: form.customDescription.trim() || null,
    };

    if (mode === 'create') {
      payload.applyToAllGames = !!form.applyToAllGames;
      payload.gameId = form.applyToAllGames ? null : form.gameId || null;
      // matchScope is meaningful any time MATCH_THRESHOLD is selected — single-game
      // or fan-out. Server uses it to decide whether to emit 1 or 2 props per game.
      if (form.propType === 'MANUAL_MATCH_THRESHOLD') {
        payload.matchScope = form.matchScope;
      }
    } else {
      payload.gameId = form.gameId || null;
    }

    try {
      const url =
        mode === 'create'
          ? `/api/admin/main-events/${mainEventId}/undercards/${undercardId}/props/manual`
          : `/api/admin/main-events/${mainEventId}/undercards/${undercardId}/props/${existingProp!.id}/manual`;
      const method = mode === 'create' ? 'POST' : 'PATCH';
      const res = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        const data = await res.json();
        const created = data?.created;
        const successMsg =
          mode === 'create'
            ? created && created > 1
              ? `Created ${created} predictions across all games`
              : 'Manual prediction added'
            : 'Manual prediction updated';
        onSuccess(successMsg);
        onClose();
      } else {
        const data = await res.json().catch(() => ({}));
        setSubmitError(data?.error || `Failed to ${mode === 'create' ? 'add' : 'update'} prediction`);
      }
    } catch {
      setSubmitError(`Failed to ${mode === 'create' ? 'add' : 'update'} prediction`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
      {/* Admin surfaces keep their zinc/amber chrome — only the chassis moved.
          The <form> is now inside the panel rather than being the panel, so
          the header/body/footer stack exactly as before. */}
      <SheetContent
        className="max-w-2xl border-amber-500/30 bg-zinc-900"
        showClose={false}
      >
        <form onSubmit={handleSubmit}>
        <div className="p-4 border-b border-zinc-800 flex shrink-0 items-center justify-between">
          <div className="flex items-center gap-2">
            {mode === 'edit' ? <Pencil className="h-4 w-4 text-amber-400" /> : <Plus className="h-4 w-4 text-amber-400" />}
            <SheetTitle className="text-base font-bold text-amber-300">
              {mode === 'edit' ? 'Edit Manual Prediction' : 'Add Manual Prediction'}
            </SheetTitle>
          </div>
          <SheetCloseButton className="-my-2 -mr-2 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-300" />
        </div>

        <div className="p-4 space-y-4">
          {/* Template selector (create only — propType is locked in edit) */}
          {mode === 'create' && (
            <div>
              <label className="block text-xs text-zinc-400 mb-1.5">Template *</label>
              <div className="grid grid-cols-4 gap-2">
                {MATCH_TEMPLATES.map((tpl) => (
                  <button
                    key={tpl.value}
                    type="button"
                    onClick={() => setPropType(tpl.value)}
                    className={cn(
                      'px-2 py-2 rounded-lg border text-left transition-colors',
                      form.propType === tpl.value
                        ? 'bg-amber-500/10 border-amber-500/50'
                        : 'bg-zinc-950 border-zinc-700 hover:border-zinc-600'
                    )}
                  >
                    <div className="text-[11px] font-bold text-white truncate">{tpl.label}</div>
                    <div className="text-[10px] text-zinc-400 mt-0.5 leading-tight">{tpl.hint}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Match scope selector — for MATCH_THRESHOLD; affects single-game AND fan-out */}
          {mode === 'create' && form.propType === 'MANUAL_MATCH_THRESHOLD' && (
            <div>
              <label className="block text-xs text-zinc-400 mb-1.5">Match scope *</label>
              <div className="grid grid-cols-3 gap-2">
                {([
                  {
                    v: 'EITHER_TEAM' as MatchScope,
                    l: 'Either team',
                    h: '"X or Y record N+ stat?"',
                    perMatch: 1,
                  },
                  {
                    v: 'EACH_TEAM' as MatchScope,
                    l: 'Each team separately',
                    h: 'One prediction per team',
                    perMatch: 2,
                  },
                  {
                    v: 'COMBINED_TOTAL' as MatchScope,
                    l: 'Combined match total',
                    h: '"N+ stat in this match?"',
                    perMatch: 1,
                  },
                ]).map((opt) => {
                  const matchCount = form.applyToAllGames ? games.length : 1;
                  const totalPredictions = matchCount * opt.perMatch;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setMatchScope(opt.v)}
                      className={cn(
                        'px-2 py-2 rounded-lg border text-left transition-colors',
                        form.matchScope === opt.v
                          ? 'bg-amber-500/10 border-amber-500/50'
                          : 'bg-zinc-950 border-zinc-700 hover:border-zinc-600'
                      )}
                    >
                      <div className="text-[11px] font-bold text-white truncate">{opt.l}</div>
                      <div className="text-[10px] text-zinc-400 mt-0.5 leading-tight">{opt.h}</div>
                      <div className="text-[10px] text-amber-300/80 mt-0.5">
                        → {totalPredictions} prediction{totalPredictions === 1 ? '' : 's'}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Game selector (hidden when fan-out is on) */}
          {showGameSelector && (
            <div>
              <label className="block text-xs text-zinc-400 mb-1">
                Game {form.propType === 'MANUAL_MATCH_THRESHOLD' ? '*' : '(leave blank for round-wide)'}
              </label>
              <select
                value={form.gameId}
                onChange={(e) => setForm((f) => ({ ...f, gameId: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg bg-zinc-950 border border-zinc-700 text-white text-sm focus:border-amber-500 focus:outline-none"
              >
                <option value="">— None (applies to whole round) —</option>
                {games.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.awayTeam} @ {g.homeTeam}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Fan-out checkbox (only for MATCH_THRESHOLD + Custom) */}
          {mode === 'create' && fanOutEligible && (
            <label className="flex items-start gap-2 p-3 rounded-lg bg-zinc-950 border border-zinc-700 cursor-pointer hover:border-zinc-600">
              <input
                type="checkbox"
                checked={form.applyToAllGames}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    applyToAllGames: e.target.checked,
                    gameId: e.target.checked ? '' : f.gameId,
                    // The server rejects a named team alongside fan-out rather
                    // than silently ignoring it, so drop a team typed before
                    // the box was ticked.
                    subjectTeam:
                      e.target.checked && f.propType === 'MANUAL_TEAM_THRESHOLD' ? '' : f.subjectTeam,
                  }))
                }
                className="mt-0.5"
              />
              <div className="flex-1">
                <div className="text-xs font-bold text-white">
                  {form.propType === 'MANUAL_TEAM_THRESHOLD'
                    ? `Ask this of every team — all ${games.length} games in this round`
                    : `Apply to all ${games.length} games in this round`}
                </div>
                <div className="text-[10px] text-zinc-400 mt-0.5">
                  {form.propType === 'MANUAL_TEAM_THRESHOLD'
                    ? `Creates ${games.length * 2} predictions, one per team, each settling off its own box score.`
                    : 'Multiplies the prediction count above across every match.'}
                </div>
              </div>
            </label>
          )}

          {/* Quick presets — Custom + applyToAllGames flow */}
          {mode === 'create' && (
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <Sparkles className="h-3 w-3 text-amber-400" />
                <label className="block text-xs text-zinc-400">Quick presets</label>
                <span className="text-[10px] text-zinc-600">(prefills Custom + Apply-to-all-games with base-rate-tuned multiplier)</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {sport === 'NFL' && NFL_PROP_PRESETS.map((preset) => (
                  <button
                    key={preset.key}
                    type="button"
                    onClick={() =>
                      setForm((f) => ({
                        ...f,
                        // Carry the STRUCTURED fields — this is what makes the
                        // prop auto-settleable. Blanking them (as the legacy
                        // presets do) produces a prop nothing can resolve.
                        propType: preset.propType,
                        statCategory: preset.statCategory,
                        threshold: String(preset.defaultThreshold),
                        // Every game-level preset names a stat that ADDS both
                        // teams, so the prop is a match total. EACH_TEAM would
                        // re-scope the category underneath the preset, and
                        // EITHER_TEAM would leave the scope selector claiming
                        // "X or Y record N+" while the question renders — and
                        // settles — as a combined total.
                        matchScope: 'COMBINED_TOTAL' as MatchScope,
                        applyToAllGames: false,
                        subjectTeam: '',
                        subjectPlayer: '',
                        playerId: '',
                        playerPosition: '',
                        customDescription: '',
                        costCoins: preset.suggestedCostCoins,
                        bonusMultiplier: preset.suggestedMultiplier,
                      }))
                    }
                    className="px-2 py-2 rounded-lg border border-zinc-700 hover:border-amber-500/50 bg-zinc-950 text-left transition-colors"
                  >
                    <div className="text-[11px] font-bold text-white truncate">{preset.label}</div>
                    <div className="text-[10px] text-amber-400/80 mt-0.5 leading-tight">
                      line {preset.defaultThreshold} · {preset.suggestedMultiplier}× boost · auto-settles
                    </div>
                    <div className="text-[10px] text-zinc-500 leading-tight">{preset.hint}</div>
                  </button>
                ))}
                {sport !== 'NFL' && MANUAL_PROP_PRESETS.map((preset) => (
                  <button
                    key={preset.key}
                    type="button"
                    onClick={() =>
                      setForm((f) => ({
                        ...f,
                        propType: 'MANUAL_GENERIC',
                        applyToAllGames: true,
                        gameId: '',
                        subjectTeam: '',
                        subjectPlayer: '',
                        playerId: '',
                        playerPosition: '',
                        threshold: '',
                        statCategory: '',
                        customDescription: preset.description,
                        costCoins: preset.suggestedCostCoins,
                        bonusMultiplier: preset.suggestedMultiplier,
                      }))
                    }
                    className="px-2 py-2 rounded-lg border border-zinc-700 hover:border-amber-500/50 bg-zinc-950 text-left transition-colors"
                  >
                    <div className="text-[11px] font-bold text-white truncate">{preset.label}</div>
                    <div className="text-[10px] text-zinc-400 mt-0.5 leading-tight">
                      ~{Math.round(preset.baseRate * 100)}% base rate · {preset.suggestedMultiplier}× boost
                    </div>
                    <div className="text-[10px] text-zinc-500 leading-tight">{preset.hint}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Subject Team */}
          {showSubjectTeam && (
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Team *</label>
              <input
                type="text"
                value={form.subjectTeam}
                onChange={(e) => setForm((f) => ({ ...f, subjectTeam: e.target.value }))}
                placeholder="Argentina"
                className="w-full px-3 py-2 rounded-lg bg-zinc-950 border border-zinc-700 text-white text-sm focus:border-amber-500 focus:outline-none"
              />
            </div>
          )}

          {/* Subject Player — a bound roster row for NFL, free text elsewhere */}
          {showSubjectPlayer && (
            sport === 'NFL' ? (
              <NflPlayerPicker
                value={form.playerId}
                displayName={form.subjectPlayer}
                boundPosition={form.playerPosition || undefined}
                positions={selectedStat?.positions}
                statLabel={selectedStat?.label}
                onChange={(player) =>
                  setForm((f) => ({
                    ...f,
                    playerId: player?.id ?? '',
                    subjectPlayer: player?.name ?? '',
                    playerPosition: player?.position ?? '',
                  }))
                }
              />
            ) : (
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Player *</label>
                <input
                  type="text"
                  value={form.subjectPlayer}
                  onChange={(e) => setForm((f) => ({ ...f, subjectPlayer: e.target.value }))}
                  placeholder="Lionel Messi"
                  className="w-full px-3 py-2 rounded-lg bg-zinc-950 border border-zinc-700 text-white text-sm focus:border-amber-500 focus:outline-none"
                />
              </div>
            )
          )}

          {/* Threshold + Stat */}
          {showThresholdField && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-zinc-400 mb-1">
                  {form.propType === 'MANUAL_PLAYER_BOOLEAN' ? 'Threshold (blank = any)' : 'Threshold *'}
                </label>
                <input
                  type="number"
                  step={selectedStat?.step ?? 0.5}
                  value={form.threshold}
                  onChange={(e) => setForm((f) => ({ ...f, threshold: e.target.value }))}
                  placeholder={form.propType === 'MANUAL_PLAYER_BOOLEAN' ? '1' : '2'}
                  className="w-full px-3 py-2 rounded-lg bg-zinc-950 border border-zinc-700 text-white text-sm focus:border-amber-500 focus:outline-none"
                />
              </div>
              {showStatField && (
                <div>
                  <label className="block text-xs text-zinc-400 mb-1">Stat *</label>
                  <select
                    value={form.statCategory}
                    onChange={(e) => setStatCategory(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-zinc-950 border border-zinc-700 text-white text-sm focus:border-amber-500 focus:outline-none"
                  >
                    {statOptions.map((o) => (
                      <option key={o.id} value={o.id}>{o.label}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {/* What resolves the chosen stat — auto-settles, or waits for an admin. */}
          {showStatField && selectedStat?.note && (
            <div className="-mt-2 text-[10px] leading-tight text-zinc-500">
              <span
                className={cn(
                  'font-bold',
                  selectedStat.settlement === 'AUTO' ? 'text-emerald-400/80' : 'text-amber-400/80'
                )}
              >
                {selectedStat.settlement === 'AUTO' ? 'Auto-settles' : 'Manual settle'}
              </span>
              {' · '}
              {selectedStat.note}
            </div>
          )}

          {/* Description (override or primary input) */}
          <div>
            <label className="block text-xs text-zinc-400 mb-1">
              {customIsRequired ? 'Description *' : 'Description override (leave blank to use template)'}
            </label>
            <input
              type="text"
              value={form.customDescription}
              onChange={(e) => setForm((f) => ({ ...f, customDescription: e.target.value }))}
              placeholder={customIsRequired ? 'Will the match go to penalties?' : '(auto-generated from template)'}
              className="w-full px-3 py-2 rounded-lg bg-zinc-950 border border-zinc-700 text-white text-sm focus:border-amber-500 focus:outline-none"
            />
          </div>

          {/* Live preview */}
          <div className="p-3 rounded-lg bg-zinc-950/50 border border-zinc-700 min-h-[3rem]">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Preview</div>
            {previewLines.length === 0 ? (
              <div className="text-sm text-zinc-600 italic">Fill in the fields above…</div>
            ) : (
              <div className="space-y-1">
                {previewLines.map((line, i) => (
                  <div key={i} className="text-sm text-amber-300 font-medium leading-tight">
                    {line}
                  </div>
                ))}
              </div>
            )}
            {form.applyToAllGames && games.length > 0 && (
              <div className="mt-2 text-[10px] text-zinc-500">
                {teamIsPerGame
                  ? `Repeated for both teams in all ${games.length} matches — ${games.length * 2} predictions.`
                  : `Same template applies to all ${games.length} matches in this round.`}
              </div>
            )}
          </div>

          {/* Cost / Multiplier + payout preview */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Cost (coins) *</label>
              <input
                type="number"
                value={form.costCoins}
                onChange={(e) => setForm((f) => ({ ...f, costCoins: Number(e.target.value) }))}
                min={1}
                className="w-full px-3 py-2 rounded-lg bg-zinc-950 border border-zinc-700 text-white text-sm focus:border-amber-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Bonus Multiplier *</label>
              <input
                type="number"
                step="0.1"
                value={form.bonusMultiplier}
                onChange={(e) => setForm((f) => ({ ...f, bonusMultiplier: Number(e.target.value) }))}
                min={0.1}
                className="w-full px-3 py-2 rounded-lg bg-zinc-950 border border-zinc-700 text-white text-sm focus:border-amber-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Bonus on win</label>
              <div className="px-3 py-2 rounded-lg bg-zinc-950 border border-zinc-700 text-sm font-mono text-emerald-300">
                +{estimatedPoints.toFixed(2)} points
              </div>
              <div className="text-[10px] text-zinc-500 mt-1 leading-tight">
                Boosts a correct pick from {pointsPerCorrectPick} to{' '}
                {boostedTotal.toFixed(2)} ({form.bonusMultiplier}×). Pays nothing if the
                pick was wrong.
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-zinc-800 shrink-0 space-y-2">
          {submitError && (
            <p role="alert" className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
              {submitError}
            </p>
          )}
          <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm text-zinc-400 hover:text-zinc-200">
            Cancel
          </button>
          <Button type="submit" size="sm" disabled={submitting}>
            {submitting ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Plus className="h-3 w-3 mr-1" />}
            {mode === 'edit' ? 'Save Changes' : 'Add Prediction'}
          </Button>
          </div>
        </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
